import { useCallback, useEffect, useState, type FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Clock } from 'lucide-react';
import { t } from '../i18n';
import { markAnalysisWatchedApi } from '../api/client';

/*
 * Yiqilgan urinishdan keyingi kutish (server: progress/retry-policy.ts).
 *
 * Avtomatik testda xato raqamlari koʻrsatiladi; cheksiz urinishda oʻquvchi
 * bilmasa ham harflarni almashtirib oʻtib ketardi. Endi keyingi urinish
 * kursdagi kutishdan keyin — yoki tahlil videosi koʻrilsa, darhol. Server
 * baribir rad etadi; bu yerda — oʻquvchi nima uchun kutayotganini koʻrsin.
 */

/** Kutish hali tugamagan boʻlsa — qachongacha; tugaganda oʻzi qayta chizadi. */
export function useRetryWait(retryAfter?: string | null): Date | null {
  const until = retryAfter ? new Date(retryAfter) : null;
  const ms = until ? until.getTime() - Date.now() : 0;
  const [, tick] = useState(0);
  useEffect(() => {
    if (ms <= 0) return;
    // setTimeout 24.8 kundan ortigʻini koʻtarmaydi; kutish 72 soatgacha.
    const id = window.setTimeout(() => tick((n) => n + 1), ms + 500);
    return () => window.clearTimeout(id);
  }, [ms]);
  return until && ms > 0 ? until : null;
}

/** `14:30` yoki boshqa kun boʻlsa `24.10 14:30` (qurilma vaqti). */
function formatUntil(d: Date): string {
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
  if (d.toDateString() === new Date().toDateString()) return hm;
  return `${String(d.getDate()).padStart(2, '0')}.${String(
    d.getMonth() + 1,
  ).padStart(2, '0')} ${hm}`;
}

export const RetryWaitNote: FC<{ until: Date; hasAnalysis: boolean }> = ({
  until,
  hasAnalysis,
}) => (
  <div className="callout callout-warning">
    <Clock size={17} style={{ flex: 'none' }} aria-hidden="true" />
    <span>
      {t('Keyingi urinish {time} da ochiladi.', { time: formatUntil(until) })}
      {hasAnalysis &&
        ` ${t('Tahlil videosini oxirigacha koʻrsangiz — darhol ochiladi.')}`}
    </span>
  </div>
);

/** Tahlil videosi 80% koʻrilganda chaqiriladi (AnalysisVideo `onWatched`). */
export function useAnalysisWatched(lessonId: string, waiting: boolean) {
  const queryClient = useQueryClient();
  return useCallback(
    (percent: number) => {
      if (!waiting) return;
      markAnalysisWatchedApi(lessonId, percent)
        .then((r) => {
          if (!r?.retryOpen) return;
          toast.success(t('Endi qayta topshirishingiz mumkin.'));
          void queryClient.invalidateQueries({ queryKey: ['course-progress'] });
        })
        .catch(() => {
          /* fon soʻrovi — kutish oʻz vaqtida baribir tugaydi */
        });
    },
    [lessonId, waiting, queryClient],
  );
}
